// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import User from "./user.js"

const modelClassData = {
  "attributes": {
    "created_at": {
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      },
      "name": "created_at",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    },
    "status_code": {
      "column": {
        "default": null,
        "name": "status_code",
        "null": false,
        "type": "integer"
      },
      "name": "status_code",
      "selected_by_default": null,
      "translated": null
    },
    "updated_at": {
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      },
      "name": "updated_at",
      "selected_by_default": null,
      "translated": null
    },
    "url": {
      "column": {
        "default": null,
        "name": "url",
        "null": false,
        "type": "string"
      },
      "name": "url",
      "selected_by_default": null,
      "translated": null
    },
    "user_id": {
      "column": {
        "default": null,
        "name": "user_id",
        "null": true,
        "type": null
      },
      "name": "user_id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "PageView",
  "collectionKey": "page_views",
  "collectionName": "page_views",
  "i18nKey": "page_view",
  "camelizedLower": "pageView",
  "name": "PageView",
  "nameDasherized": "page-view",
  "pluralName": "page_views",
  "ransackable_associations": [
    {
      "className": "PageView::Audit",
      "collectionName": "page_view_audits",
      "foreignKey": "page_view_id",
      "name": "audits",
      "macro": "has_many",
      "resource_name": "PageViewAudit",
      "through": null
    },
    {
      "className": "PageView::Audit",
      "collectionName": "page_view_audits",
      "foreignKey": "page_view_id",
      "name": "create_audit",
      "macro": "has_one",
      "resource_name": "PageViewAudit",
      "through": null
    },
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": null
      }
    },
    {
      "name": "status_code",
      "column": {
        "default": null,
        "name": "status_code",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "url",
      "column": {
        "default": null,
        "name": "url",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "user_id",
      "column": {
        "default": null,
        "name": "user_id",
        "null": true,
        "type": null
      }
    }
  ],
  "ransackable_scopes": [],
  "relationships": [
    {
      "className": "User",
      "collectionName": "users",
      "foreignKey": "user_id",
      "name": "user",
      "macro": "belongs_to",
      "resource_name": "User",
      "through": null
    }
  ],
  "paramKey": "page_view",
  "primaryKey": "id"
}

/** Frontend model for PageView. */
export default class PageView extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {Date} */
  createdAt() {
    return this.readAttributeUnderscore("created_at")
  }

  /** @returns {boolean} */
  hasCreatedAt() {
    const value = this.createdAt()

    return this._isPresent(value)
  }

  /** @returns {any} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {number} */
  statusCode() {
    return this.readAttributeUnderscore("status_code")
  }

  /** @returns {boolean} */
  hasStatusCode() {
    const value = this.statusCode()

    return this._isPresent(value)
  }

  /** @returns {Date} */
  updatedAt() {
    return this.readAttributeUnderscore("updated_at")
  }

  /** @returns {boolean} */
  hasUpdatedAt() {
    const value = this.updatedAt()

    return this._isPresent(value)
  }

  /** @returns {string} */
  url() {
    return this.readAttributeUnderscore("url")
  }

  /** @returns {boolean} */
  hasUrl() {
    const value = this.url()

    return this._isPresent(value)
  }

  /** @returns {any | null} */
  userId() {
    return this.readAttributeUnderscore("user_id")
  }

  /** @returns {boolean} */
  hasUserId() {
    const value = this.userId()

    return this._isPresent(value)
  }

  /** @returns {import("./user.js").default | null} */
  user() {
    return this._readBelongsToReflection({modelClass: User, reflectionName: "user"})
  }

  /** @returns {Promise<import("./user.js").default | null>} */
  loadUser() {
    if (!("userId" in this)) throw new Error("Foreign key method wasn't defined: userId")

    const id = this.userId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "user",
        model: this,
        modelClass: User
      },
      {ransack}
    )
  }
}
